/** Hotel setup queries: the curated operational facts for a hotel. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type HotelFactsResponse, hotelFactsResponseSchema } from '../responses';
import { unwrap } from './_helpers';

/**
 * `GET /setup/facts` — the hotel's saved facts plus the category metadata the UI
 * needs to render the form (`requiredCategories` for the activation gate,
 * `allowedCategories` for the full curated set).
 */
export function useHotelFacts(
  tenantId: string,
  hotelId: string,
): UseQueryResult<HotelFactsResponse> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.hotelFacts(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}/setup/facts', {
        params: { path: { tenantId, hotelId } },
        parseAs: 'json',
      });
      return unwrap(result, hotelFactsResponseSchema);
    },
    enabled: hotelId.length > 0,
  });
}
